import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { View, ScrollView, StyleSheet } from "react-native";
import { BlockPlanificationConstants } from "../../../Resources/Constants";

/**
 * VirtualizedWeekColumns
 *
 * A horizontally virtualized list of week columns that maintains natural page scroll.
 * Uses scroll position tracking to only render visible columns.
 *
 * Key difference from react-window: No fixed height requirement.
 * The content flows naturally with the page, only horizontal scrolling is virtualized.
 *
 * Features:
 * - Horizontal virtualization with fixed column widths
 * - Maintains natural page scroll (no fixed height container)
 * - Exposes scroll position for header synchronization
 * - Compatible with react-dnd drag context
 * - Robust scroll tracking with multiple fallback mechanisms
 */

const COLUMN_GAP = BlockPlanificationConstants.COLUMN_GAP;

// Overscan: render extra columns outside viewport for smoother scrolling
const OVERSCAN_COUNT = 3;

/**
 * Calculate which columns are visible based on scroll position and container width
 */
const getVisibleRange = (
  scrollLeft,
  containerWidth,
  totalColumns,
  totalColumnWidth,
  overscan = OVERSCAN_COUNT
) => {
  // Always render a reasonable default if we can't determine visibility
  if (totalColumns === 0) {
    return { start: 0, end: 0 };
  }

  // If container width isn't measured yet, render first several columns
  if (containerWidth === 0) {
    return { start: 0, end: Math.min(6, totalColumns) };
  }

  const startIndex = Math.floor(scrollLeft / totalColumnWidth);
  const visibleCount = Math.ceil(containerWidth / totalColumnWidth) + 1;
  const endIndex = startIndex + visibleCount;

  return {
    start: Math.max(0, startIndex - overscan),
    end: Math.min(totalColumns, endIndex + overscan),
  };
};

/**
 * Placeholder component - takes up space but renders nothing
 */
const ColumnPlaceholder = ({ width }) => (
  <View style={{ width, flexShrink: 0 }} />
);

/**
 * VirtualizedWeekColumns Component
 *
 * @param {Object} props
 * @param {number[]} props.weeks - Array of week numbers to render
 * @param {Function} props.renderColumn - Render function (week, index) => React.Node
 * @param {Function} props.onScroll - Callback when scroll position changes (scrollLeft)
 * @param {Function} props.setScrollViewRef - Callback to expose the ScrollView ref
 * @param {Object} props.style - Additional container styles
 * @param {number} [props.columnWidth] - Column width (defaults to BlockPlanificationConstants.COLUMN_WIDTH)
 * @param {Function} [props.onContainerWidthChange] - Callback with container width
 * @param {number} [props.minContentHeight] - Minimum height for the content container
 */
const VirtualizedWeekColumns = ({
  weeks,
  renderColumn,
  onScroll,
  setScrollViewRef,
  style,
  columnWidth = BlockPlanificationConstants.COLUMN_WIDTH,
  onContainerWidthChange,
  minContentHeight,
}) => {
  const scrollViewRef = useRef(null);
  const domElementRef = useRef(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const rafRef = useRef(null);
  const isMountedRef = useRef(true);
  const touchStartRef = useRef(null);

  const totalColumnWidth = columnWidth + COLUMN_GAP;

  // Calculate visible range
  const { start: startIndex, end: endIndex } = useMemo(
    () =>
      getVisibleRange(
        scrollLeft,
        containerWidth,
        weeks?.length || 0,
        totalColumnWidth
      ),
    [scrollLeft, containerWidth, weeks?.length, totalColumnWidth]
  );

  // Get the actual DOM element from a React Native Web ref
  const getDOMElement = useCallback((ref) => {
    if (!ref) return null;
    // Direct DOM element check
    if (
      ref.scrollLeft !== undefined &&
      typeof ref.getBoundingClientRect === "function"
    ) {
      return ref;
    }
    // React Native Web internal component
    if (ref._component) return ref._component;
    // Older React Native Web API
    if (typeof ref.getNode === "function") {
      try {
        return ref.getNode();
      } catch (e) {
        return null;
      }
    }
    // Try to find scrollable child (React Native Web wraps in a div)
    if (ref.firstChild && ref.firstChild.scrollLeft !== undefined) {
      return ref.firstChild;
    }
    return ref;
  }, []);

  const updateContainerWidth = useCallback(
    (nextWidth) => {
      if (!Number.isFinite(nextWidth) || nextWidth <= 0) return;
      setContainerWidth((prev) => (prev !== nextWidth ? nextWidth : prev));
      onContainerWidthChange?.(nextWidth);
    },
    [onContainerWidthChange]
  );

  /**
   * Read current scroll position from DOM and update state
   * Called inside RAF for batched state updates
   */
  const updateScrollState = useCallback(() => {
    if (!isMountedRef.current) return;

    const element = domElementRef.current;
    if (!element) return;

    const newScrollLeft = element.scrollLeft || 0;
    setScrollLeft(newScrollLeft);
  }, []);

  /**
   * Scroll handler - calls onScroll IMMEDIATELY for header sync,
   * then defers state update to RAF for virtualization
   */
  const handleDOMScroll = useCallback(() => {
    const element = domElementRef.current;
    if (!element) return;

    // IMMEDIATELY call onScroll for header sync (no RAF delay)
    const currentScrollLeft = element.scrollLeft || 0;
    onScroll?.(currentScrollLeft);

    // Defer state update to RAF for virtualization (batched)
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(updateScrollState);
  }, [onScroll, updateScrollState]);

  // Setup DOM scroll listener and measurements
  useEffect(() => {
    isMountedRef.current = true;

    // Wait for next frame to ensure DOM is ready
    const setupTimer = requestAnimationFrame(() => {
      if (!isMountedRef.current) return;

      const element = getDOMElement(scrollViewRef.current);
      if (!element) return;

      domElementRef.current = element;

      // Measure initial width
      const measureWidth = () => {
        if (!isMountedRef.current) return;
        const rect = element.getBoundingClientRect();
        if (rect.width > 0) {
          updateContainerWidth(rect.width);
        }
      };

      // Initial measurement
      measureWidth();

      // Initial scroll position read (also triggers onScroll for header)
      const initialScrollLeft = element.scrollLeft || 0;
      setScrollLeft(initialScrollLeft);
      onScroll?.(initialScrollLeft);

      // Setup resize observer for container width
      let resizeObserver = null;
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => {
          measureWidth();
          // Re-check scroll position on resize
          const currentScrollLeft = element.scrollLeft || 0;
          setScrollLeft(currentScrollLeft);
          onScroll?.(currentScrollLeft);
        });
        resizeObserver.observe(element);
      }

      // Setup direct DOM scroll listener (more reliable than RN onScroll)
      element.addEventListener("scroll", handleDOMScroll, { passive: true });

      // Store cleanup references
      scrollViewRef.current._cleanup = () => {
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        element.removeEventListener("scroll", handleDOMScroll);
      };
    });

    return () => {
      isMountedRef.current = false;
      cancelAnimationFrame(setupTimer);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (scrollViewRef.current?._cleanup) {
        scrollViewRef.current._cleanup();
      }
    };
  }, [getDOMElement, handleDOMScroll, onScroll, updateContainerWidth]);

  // Handle React Native onScroll as backup
  const handleRNScroll = useCallback(
    (event) => {
      const newScrollLeft = event?.nativeEvent?.contentOffset?.x ?? 0;
      setScrollLeft(newScrollLeft);
      onScroll?.(newScrollLeft);
    },
    [onScroll]
  );

  // Callback ref for ScrollView
  const handleScrollViewRef = useCallback(
    (el) => {
      scrollViewRef.current = el;
      if (setScrollViewRef) {
        const domNode = getDOMElement(el);
        domElementRef.current = domNode;
        setScrollViewRef(domNode);

        // Immediately measure if we have a valid element
        if (domNode) {
          const rect = domNode.getBoundingClientRect();
          if (rect.width > 0) {
            updateContainerWidth(rect.width);
          }
        }
      }
    },
    [getDOMElement, setScrollViewRef, updateContainerWidth]
  );

  // Guard: don't render if weeks is empty
  if (!weeks || weeks.length === 0) {
    return <View style={[styles.container, style]} />;
  }

  // Calculate total content width
  const totalWidth = weeks.length * totalColumnWidth - COLUMN_GAP;

  // Calculate spacer widths for before and after visible columns
  const beforeWidth = startIndex * totalColumnWidth;
  const afterWidth = Math.max(
    0,
    (weeks.length - endIndex) * totalColumnWidth - COLUMN_GAP
  );

  // Build the visible columns
  const visibleColumns = [];
  for (let i = startIndex; i < endIndex; i++) {
    const week = weeks[i];
    if (week === undefined) continue;

    const isLast = i === weeks.length - 1;

    visibleColumns.push(
      <View
        key={`week-${week}`}
        style={[
          styles.columnWrapper,
          { width: columnWidth, marginRight: isLast ? 0 : COLUMN_GAP },
        ]}
      >
        {renderColumn(week, i)}
      </View>
    );
  }

  return (
    <ScrollView
      ref={handleScrollViewRef}
      horizontal
      style={[styles.scrollView, style]}
      contentContainerStyle={[
        styles.contentContainer,
        { minWidth: totalWidth, minHeight: minContentHeight || 0 },
      ]}
      showsHorizontalScrollIndicator={false}
      scrollEventThrottle={16}
      onScroll={handleRNScroll}
    >
      {/* Spacer for columns before visible range */}
      {beforeWidth > 0 && <ColumnPlaceholder width={beforeWidth} />}

      {/* Visible columns */}
      {visibleColumns}

      {/* Spacer for columns after visible range */}
      {afterWidth > 0 && <ColumnPlaceholder width={afterWidth} />}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
    // Apply overscroll-behavior-x via style prop as fallback
    // Note: React Native Web may not support this, so we also apply it via DOM manipulation
    // Only prevent horizontal overscroll, allow vertical scrolling to work normally
  },
  contentContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  columnWrapper: {
    flexShrink: 0,
  },
});

export default VirtualizedWeekColumns;
